import DOMPurify from "dompurify";
import {
  ArrowUp,
  BookOpen,
  ChevronLeft,
  MessageCircle,
  Minus,
  Plus,
  RotateCcw,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  X,
} from "lucide-react";
import { marked } from "marked";
import { useMemo, useState } from "react";

type ChatWidgetPreviewProps = {
  assistantName?: string;
  greeting?: string;
  accent?: string;
  embedded?: boolean;
  historyEnabled?: boolean;
  feedbackEnabled?: boolean;
};

const welcomeSuggestions = [
  "Como emitir uma NFS-e?",
  "Onde altero a empresa ativa?",
];

export function ChatWidgetPreview({
  assistantName = "Dora",
  greeting = "Olá! Como posso ajudar no Simplifica?",
  accent = "#6238f5",
  embedded = false,
  historyEnabled = true,
  feedbackEnabled = true,
}: ChatWidgetPreviewProps) {
  const [open, setOpen] = useState(true);
  const [sourceOpen, setSourceOpen] = useState(false);
  const [sentQuestion, setSentQuestion] = useState("");
  const [question, setQuestion] = useState("");
  const [conversationStarted, setConversationStarted] = useState(false);
  const [feedback, setFeedback] = useState<"up" | "down" | null>(null);
  const answer = useMemo(
    () =>
      DOMPurify.sanitize(
        marked.parse(
          sentQuestion === welcomeSuggestions[1]
            ? "Para alterar a **empresa ativa**, abra o seletor de empresa no cabeçalho e escolha uma empresa à qual você tem acesso. Confira o nome antes de continuar. [Exemplo fictício]"
            : sentQuestion === welcomeSuggestions[0]
              ? "Para emitir uma **NFS-e**, abra **Fiscal → Notas de serviço** e selecione **Nova nota**.\n\n1. Confirme a empresa ativa.\n2. Escolha o cliente e o serviço.\n3. Revise os impostos antes de transmitir. [Exemplo fictício]"
              : "Esta é uma demonstração local. Escolha uma das duas perguntas sugeridas para ver uma resposta de exemplo; perguntas livres não consultam uma base real.",
          { async: false },
        ),
      ),
    [sentQuestion],
  );

  function submit(nextQuestion?: string) {
    const text = nextQuestion || question;
    if (!text.trim()) return;
    setQuestion(text);
    setSentQuestion(text);
    setFeedback(null);
    setSourceOpen(false);
    setConversationStarted(true);
  }

  function resetConversation() {
    setConversationStarted(false);
    setQuestion("");
    setSentQuestion("");
    setSourceOpen(false);
    setFeedback(null);
  }

  if (!open) {
    return (
      <button
        type="button"
        className="chat-launcher"
        style={{ background: accent }}
        onClick={() => setOpen(true)}
        aria-label={`Abrir ${assistantName}`}
      >
        <MessageCircle size={24} />
      </button>
    );
  }

  return (
    <div
      className={`chat-preview ${embedded ? "chat-preview--embedded" : ""}`}
      style={{ "--chat-accent": accent } as React.CSSProperties}
    >
      <div className="chat-preview__header">
        <div className="chat-preview__avatar">
          <Sparkles size={18} />
        </div>
        <div>
          <strong>{assistantName}</strong>
          <span>
            <i /> Demonstração com conteúdo de exemplo
          </span>
        </div>
        <div className="chat-preview__actions">
          <button
            type="button"
            aria-label="Minimizar"
            onClick={() => setOpen(false)}
          >
            <Minus size={18} />
          </button>
          <button
            type="button"
            aria-label="Fechar"
            onClick={() => {
              resetConversation();
              setOpen(false);
            }}
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {!historyEnabled && (
        <small className="chat-disclaimer">
          Histórico desativado para o usuário final
        </small>
      )}
      <div className="chat-preview__body" aria-live="polite">
        {conversationStarted ? (
          <>
            <button
              className="chat-preview__back"
              type="button"
              onClick={resetConversation}
            >
              <ChevronLeft size={16} /> Nova conversa
            </button>
            <div className="chat-bubble chat-bubble--user">{sentQuestion}</div>
            <div className="chat-answer">
              <div
                className="chat-answer__content"
                dangerouslySetInnerHTML={{ __html: answer }}
              />
              <div className="chat-sources">
                <strong>
                  <BookOpen size={14} /> Fontes consultadas
                </strong>
                {welcomeSuggestions.includes(sentQuestion) ? (
                  <>
                    <button
                      type="button"
                      className="text-link"
                      onClick={() => setSourceOpen(!sourceOpen)}
                      aria-expanded={sourceOpen}
                    >
                      {sourceOpen
                        ? "Fechar fonte demonstrativa"
                        : "Abrir fonte demonstrativa"}
                    </button>
                    {sourceOpen ? (
                      <p>
                        {sentQuestion === welcomeSuggestions[0]
                          ? "Exemplo v1 — Emissão de NFS-e: Fiscal → Notas de serviço → Nova nota. Confira empresa, cliente, serviço e impostos."
                          : "Exemplo v1 — Empresas: use o seletor do cabeçalho. Apenas empresas autorizadas ficam disponíveis."}
                      </p>
                    ) : null}
                  </>
                ) : (
                  <span>Nenhuma fonte consultada.</span>
                )}
              </div>
              {feedbackEnabled && (
                <div className="chat-feedback">
                  <span>Esta resposta foi útil?</span>
                  <button
                    className={feedback === "up" ? "is-active" : ""}
                    type="button"
                    onClick={() => setFeedback("up")}
                    aria-label="Resposta útil"
                    aria-pressed={feedback === "up"}
                  >
                    <ThumbsUp size={16} />
                  </button>
                  <button
                    className={feedback === "down" ? "is-active" : ""}
                    type="button"
                    onClick={() => setFeedback("down")}
                    aria-label="Resposta não útil"
                    aria-pressed={feedback === "down"}
                  >
                    <ThumbsDown size={16} />
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="chat-greeting">
              <span className="chat-greeting__icon">
                <Sparkles size={17} />
              </span>
              <p>{greeting}</p>
            </div>
            <div className="chat-suggestions">
              <span>Sugestões</span>
              {welcomeSuggestions.map((suggestion) => (
                <button
                  type="button"
                  key={suggestion}
                  onClick={() => submit(suggestion)}
                >
                  {suggestion} <Plus size={15} />
                </button>
              ))}
            </div>
            <div className="chat-disclaimer">
              <Sparkles size={14} />
              <span>
                Posso cometer erros. Consulte as fontes antes de decisões
                importantes.
              </span>
            </div>
          </>
        )}
      </div>

      <form
        className="chat-preview__composer"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <input
          id="chat-preview-question"
          name="question"
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          placeholder="Pergunte sobre o produto..."
          aria-label="Mensagem"
          autoComplete="off"
        />
        {conversationStarted ? (
          <button
            type="button"
            onClick={resetConversation}
            aria-label="Recomeçar"
          >
            <RotateCcw size={18} />
          </button>
        ) : (
          <button type="submit" aria-label="Enviar">
            <ArrowUp size={18} />
          </button>
        )}
      </form>
    </div>
  );
}
